import { useState } from 'react';
import { Checkbox } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { ServicesDialog } from './services.dialog';
import { Customer } from '../../models/customer';
import { Icons } from '../../assets/icons';
import { GridCountCell } from '../../components/grid-cell-count';
import { UserPermissionName } from '../../models/user-permission-name';
import { useAuthorize } from '../../hooks/use-authorize';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import ConfirmDialog from '../../components/confirm-dialog';

interface CustomGridCellProps {
  field: string;
}

interface CellProps {
  customer: Customer;
}

const ServicesCountCell = ({ customer }: CellProps) => {
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  const dialogHandler = () => {
    setIsDialogVisible(!isDialogVisible);
  };

  const isServiceViewPermission = useAuthorize([UserPermissionName.VIEW_SERVICE_CUSTOMER]);

  return (
    <>
      <GridCountCell
        count={customer.servicesCount}
        icon={<DescriptionOutlinedIcon className="serviceImgIcon" />}
        onClick={dialogHandler}
        isDisabled={!isServiceViewPermission}
      />
      {isDialogVisible && (
        <ServicesDialog customerID={customer.customerID} isOpen={isDialogVisible} onCloseClick={dialogHandler} />
      )}
    </>
  );
};

const EnableEmailCell = ({ customer }: CellProps) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);

  const canEditCustomer = useAuthorize([UserPermissionName.EDIT_CUSTOMER]);
  const isReadOnlyPermission = useAuthorize([UserPermissionName.READ_ONLY]);

  const { isLoading, mutateAsync } = useSaveData<any>(
    customersApiKeyStore.enableCustomerEmails(),
    () => customersApiKeyStore.getCustomers()
  );

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPendingValue(event.target.checked);
    setIsConfirmOpen(true);
  };

  const handleClose = () => {
    setIsConfirmOpen(false);
    setPendingValue(null);
  };

  const handleConfirm = () => {
    if (pendingValue === null) return;

    mutateAsync({
      data: {
        customerID: customer.customerID,
        isReportEmailsEnabled: pendingValue,
      },
    }).then(() => {
      handleClose();
    });
  };

  const confirmMessage =
    pendingValue === true
      ? `Are you sure you want to enable report emails for ${customer.customerName}?`
      : `Are you sure you want to disable report emails for ${customer.customerName}?`;

  return (
    <td style={{ textAlign: 'center', padding: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Checkbox
          checked={!!customer.isReportEmailsEnabled}
          onChange={handleCheckboxChange}
          disabled={!canEditCustomer || isReadOnlyPermission}
          inputProps={{ 'aria-label': 'Enable Email' }}
          sx={{ padding: '4px', margin: 0 }}
        />
      </div>
      <ConfirmDialog
        open={isConfirmOpen}
        title="CONFIRM UPDATE"
        message={confirmMessage}
        onClose={handleClose}
        onConfirm={handleConfirm}
        isLoading={isLoading}
      />
    </td>
  );
};

export const CustomGridCell = ({ field }: CustomGridCellProps) => {
  const isProjectViewPermission = useAuthorize([UserPermissionName.VIEW_PROJECT]);

  if (field === 'projectsCount') {
    return ({ dataItem }: any) => {
      return (
        <GridCountCell
          count={dataItem.projectsCount}
          icon={<img src={Icons.Project} alt="Project" className="gridImgMaxHeight" />}
          route={`/projects?customerID=${dataItem.customerID}`}
          isDisabled={!isProjectViewPermission}
        />
      );
    };
  }
  if (field === 'servicesCount') {
    return (dataItem: any) => {
      return <ServicesCountCell customer={dataItem.dataItem} />;
    };
  }
  if (field === 'isReportEmailsEnabled') {
    return (dataItem: any) => {
      return <EnableEmailCell customer={dataItem.dataItem} />;
    };
  }
};
